import { DEFAULT_THUMBNAIL } from '@/constants/common'
import CalendarIcon from '@/public/images/icons/calendar.svg'
import NatureIcon from '@/public/images/icons/nature.svg'
import { Event } from '@/types/Event'
import capitalize from '@/utils/capitalize'
import { formatDate } from '@/utils/formatDate'
import { currencyFormat, numberFormat } from '@/utils/formatNumber'
import { Badge } from 'flowbite-react'
import Image from 'next/image'
import Link from 'next/link'
import React from 'react'

interface EventCardProps {
  isSpotlight?: boolean
  eventData: Event
  state: string
}

const EventCard: React.FC<EventCardProps> = ({
  state,
  isSpotlight,
  eventData,
}) => {
  const renderEventState = () => {
    const date =
      state === 'register_now'
        ? formatDate(
            eventData.registration_end_date,
            'dd MMM yy',
            eventData.zoneinfo
          )
        : formatDate(
            eventData.registration_start_date,
            'dd MMM yy',
            eventData.zoneinfo
          )

    return (
      <div
        className={`absolute z-10 top-0 mt-3 ml-3 px-3 py-1 text-sm font-medium text-gray-300 bg-gray-700 rounded-md`}
      >
        {state === 'register_now' ? `Until ${date}` : `Opens ${date}`}
      </div>
    )
  }

  const venueInfo =
    eventData.venue?.city && eventData.venue?.country
      ? ` / ${eventData.venue.city}, ${eventData.venue.country}`
      : ''

  return (
    <Link href={`/web/events/${eventData.slug}`} className="relative">
      {state === 'register_now' || state === 'upcoming'
        ? renderEventState()
        : null}
      <div className="hover:cursor-pointer relative rounded-3xl overflow-hidden border h-full">
        <div className="relative flex items-center justify-center w-full max-h-56 h-56 rounded-t-lg overflow-hidden">
          <Image
            src={eventData.thumbnail_url || DEFAULT_THUMBNAIL}
            width="550"
            height="224"
            className="w-auto max-w-max h-full lg:w-full lg:h-auto lg:max-w-full"
            alt={eventData.title}
          />
        </div>
        <div className="p-6">
          <div className={`flex gap-4 ${!isSpotlight && 'flex-col'}`}>
            <h5 className="text-xl font-semibold tracking-tight text-gray-900 dark:text-white line-clamp-2 break-words">
              {eventData.title}
            </h5>
            <div
              className={`flex flex-wrap gap-x-3 gap-y-1 justify-between flex-1 min-w-0 ${
                isSpotlight && 'flex-col'
              }`}
            >
              <span>
                <strong>
                  {currencyFormat(
                    eventData.prize_currency,
                    eventData.prize_amount
                  )}
                </strong>{' '}
                In Prizes
              </span>
              {eventData.show_participants && (
                <span>
                  <strong>
                    {numberFormat(eventData.registration_count || 0)}
                  </strong>{' '}
                  Participating
                </span>
              )}
            </div>
          </div>
          <p className="h-11 mt-2.5 line-clamp-2">{eventData.description}</p>
          <div
            className={`mb-5 mt-2.5 grid ${
              isSpotlight ? 'grid-cols-2' : 'grid-cols-1'
            } gap-4`}
          >
            <div className="inline-flex items-center gap-3">
              <Image
                src={NatureIcon}
                width={16}
                height={16}
                alt=""
                style={{
                  maxWidth: '100%',
                  height: 'auto',
                }}
              />
              <span className="leading-4 line-clamp-1">
                {capitalize(eventData.category)}
                {venueInfo}
              </span>
            </div>
            <div
              className={`inline-flex items-center ${
                isSpotlight && 'justify-end'
              } gap-3`}
            >
              <Image
                src={CalendarIcon}
                width={16}
                height={16}
                alt=""
                style={{
                  maxWidth: '100%',
                  height: 'auto',
                }}
              />
              <span className="leading-4">
                {formatDate(
                  eventData.start_date,
                  'dd MMM, yyyy',
                  eventData.zoneinfo
                )}{' '}
                -{' '}
                {formatDate(
                  eventData.end_date,
                  'dd MMM, yyyy',
                  eventData.zoneinfo
                )}
              </span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 line-clamp-2">
            {eventData.tag_list?.map((tag: string, index: number) => (
              <Badge
                key={index}
                className="px py-0.5 bg-gray-100 font-medium text-xs text-gray-900 rounded-md hover:cursor-pointer"
              >
                {capitalize(tag)}
              </Badge>
            ))}
          </div>
        </div>
      </div>
    </Link>
  )
}

export default EventCard
